// src/pages/negocio/CobrosNegocio.js
//
// Vista de abonos/pagos recibidos: histórico agregado de todos los
// abonos que se han hecho a los fiados del negocio.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, Table, TableHead, TableRow,
  TableCell, TableBody, Alert, CircularProgress, Chip,
} from '@mui/material';

import { fiados as apiFiados } from '../../api/fiadosApi';
import { formatoCOP } from '../../utils/format';

export default function CobrosNegocio() {
  const [rows, setRows]         = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');

  const cargar = useCallback(async () => {
    setError('');
    try {
      const lista = await apiFiados.listar();
      const arr = Array.isArray(lista) ? lista : (lista.items || []);

      // Traer detalle de cada fiado (con sus abonos)
      const detalles = await Promise.all(
        arr.slice(0, 100).map(f => apiFiados.detalle(f.id || f.Id).catch(() => null))
      );

      const abonos = [];
      for (const d of detalles) {
        if (!d) continue;
        const cliente = d.clienteNombre || d.ClienteNombre;
        const desc    = d.descripcion   || d.Descripcion;
        const fiadoId = d.id || d.Id;
        for (const a of (d.abonos || d.Abonos || [])) {
          abonos.push({
            id:     a.id || a.Id,
            fiadoId,
            cliente,
            desc,
            monto:  a.monto || a.Monto || 0,
            medio:  a.medio || a.Medio,
            fecha:  a.fecha || a.Fecha,
            nota:   a.nota  || a.Nota,
          });
        }
      }

      abonos.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      setRows(abonos);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar los cobros.');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const total = rows.reduce((s, a) => s + a.monto, 0);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">Cobros</Typography>
        <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Abonos recibidos</Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">Todavía no hay abonos registrados.</Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total cobrado</Typography>
              <Typography variant="h3" sx={{ fontFamily: 'Fraunces', color: 'success.main' }}>
                {formatoCOP(total)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {rows.length} abono(s) registrado(s)
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Vecino</TableCell>
                  <TableCell>Medio</TableCell>
                  <TableCell align="right">Monto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      {new Date(a.fecha).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{a.cliente}</Typography>
                      {a.desc && (
                        <Typography variant="caption" color="text.secondary">{a.desc}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {a.medio && <Chip size="small" label={a.medio} variant="outlined" />}
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 600, color: 'success.main' }}>
                        {formatoCOP(a.monto)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </Stack>
  );
}
